import { useEffect, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RepeatableList } from '@/components/ui/repeatable-list';
import { TagInput } from '@/components/ui/tag-input';
import { enumLabel, fieldHelp, fieldLabel, sectionTitle } from '@/components/partnerships/package-sheet-ui';
import { InternalBadge } from '@/components/partnerships/InternalBadge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useTranslation } from 'react-i18next';
import { useCreatePackage, useUpdatePackage, usePrograms, useMarkets, usePackages } from '@/hooks/usePartnerships';
import type { Package, PackageClientType, PackageLink, PackageVariant } from '../../../../shared/types/partnerships';
import { PACKAGE_CLIENT_TYPES } from '../../../../shared/types/partnerships';
import { normCode, SALE_SUB_TYPES, sortedTiers, TIER_ORDER, eligibilityFacets } from '../../../../shared/partnerships-packages';
import {
  PACKAGE_SHEET, getPath, setPath,
  type PackageSheetField, type PackageSheetSection,
} from '../../../../shared/partnerships-package-sheet';
import { toLines, fromLines } from '../../../../shared/text-lines';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string;
  pkg?: Package;
}

const STATUSES = ['PUBLISHED', 'DRAFT', 'ARCHIVED'] as const;
// Radix Select (>=2.x) lanza un error si un <SelectItem> tiene value="" y eso tira
// abajo toda la página (no hay ErrorBoundary). La opción "ninguno" usa este
// centinela y se traduce a '' al guardar en el form.
const NONE = '__none__';
const OTHER = '__other__';

/**
 * Desplegable para los códigos que se repiten (tier, GCS / LCS): evita "gcs" vs "GCS"
 * y errores de tipeo (feedback de Partnerships 07/10/2026). "Otro…" abre un campo
 * libre para un valor nuevo; se guarda en mayúsculas.
 */
function CodeSelect({ value, onChange, options, noneLabel, otherLabel, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
  noneLabel: string;
  otherLabel: string;
  placeholder?: string;
}) {
  const norm = normCode(value) ?? '';
  const [custom, setCustom] = useState(false);
  const isKnown = !norm || options.includes(norm);
  const showInput = custom || !isKnown;
  return (
    <div className="space-y-1.5">
      <Select
        value={showInput ? OTHER : norm || NONE}
        onValueChange={v => {
          if (v === OTHER) { setCustom(true); onChange(''); return; }
          setCustom(false);
          onChange(v === NONE ? '' : v);
        }}
      >
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>{noneLabel}</SelectItem>
          {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          <SelectItem value={OTHER}>{otherLabel}</SelectItem>
        </SelectContent>
      </Select>
      {showInput && <Input value={value} onChange={e => onChange(e.target.value.toUpperCase())} placeholder={placeholder} autoFocus={custom} />}
    </div>
  );
}

/** Sección plegable del formulario; abierta si tiene algo cargado. */
function FormSection({ title, open, badge, children }: { title: string; open: boolean; badge?: ReactNode; children: ReactNode }) {
  return (
    <details open={open} className="group rounded-md border border-border/60">
      <summary className="cursor-pointer select-none px-3 py-2 text-sm font-semibold flex items-center gap-2 list-none [&::-webkit-details-marker]:hidden">
        <span className="text-muted-foreground text-xs transition-transform group-open:rotate-90">▶</span>
        {title}
        {badge}
      </summary>
      <div className="px-3 pb-3 pt-1 space-y-3">{children}</div>
    </details>
  );
}

/** Campos fijos (los de siempre) del form; los de la ficha salen del registro PACKAGE_SHEET. */
interface FormData {
  name: string; description: string; targetAudience: string;
  programId: string; highlights: string;
  includedProducts: string; deliverables: string; pricing: string; currency: string;
  productionFee: string; mediaCommitment: string;
  notes: string; status: string;
  tier: string; saleType: string; saleSubType: string; videos: string; minCreators: string;
  sortOrder: string;
  /** Campos de la ficha, por `field.key`. */
  [sheetKey: string]: string;
}

const newId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `v-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
const emptyVariant = (): PackageVariant => ({ variantId: newId(), name: '' });
const emptyLink = (): PackageLink => ({ label: '', url: '' });

export function PackageFormDialog({ open, onOpenChange, partnerId, pkg }: Props) {
  const { t } = useTranslation();
  const isEditing = !!pkg;
  const create = useCreatePackage();
  const update = useUpdatePackage();
  const { data: programs = [] } = usePrograms(partnerId);
  const { data: markets = [] } = useMarkets();
  const { data: partnerPackages = [] } = usePackages(partnerId);
  // Opciones = las conocidas + las que ya usa este partner (sin duplicados por mayúsculas).
  const tierOptions = sortedTiers([...TIER_ORDER, ...partnerPackages.flatMap(p => [p.tier, ...(p.variants ?? []).map(v => v.tier)])]);
  const subTypeOptions = sortedTiers([...SALE_SUB_TYPES, ...partnerPackages.map(p => p.saleSubType)]);
  const facets = eligibilityFacets(partnerPackages);
  const [selectedMarkets, setSelectedMarkets] = useState<string[]>([]);
  const [variants, setVariants] = useState<PackageVariant[]>([]);
  const [links, setLinks] = useState<PackageLink[]>([]);
  const [segments, setSegments] = useState<string[]>([]);
  const [accountSizes, setAccountSizes] = useState<string[]>([]);
  const [clientType, setClientType] = useState<string>('');

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm<FormData>();
  const statusValue = watch('status');
  const programValue = watch('programId');

  useEffect(() => {
    if (open) {
      setSelectedMarkets(pkg?.availableMarkets || []);
      setVariants(pkg?.variants ?? []);
      setLinks(pkg?.links ?? []);
      setSegments(pkg?.eligibility?.segments ?? []);
      setAccountSizes(pkg?.eligibility?.accountSizes ?? []);
      setClientType(pkg?.eligibility?.clientType ?? '');
      const sheet: Record<string, string> = {};
      for (const s of PACKAGE_SHEET) for (const f of s.fields) {
        const v = pkg ? getPath(pkg, f.path) : undefined;
        sheet[f.key] = v == null ? '' : String(v);
      }
      reset({
        name: pkg?.name || '', description: pkg?.description || '',
        targetAudience: pkg?.targetAudience || '', programId: pkg?.programId || '',
        highlights: toLines(pkg?.highlights),
        includedProducts: toLines(pkg?.includedProducts),
        deliverables: toLines(pkg?.deliverables),
        pricing: pkg?.pricing || '',
        currency: pkg?.currency || 'USD',
        productionFee: pkg?.productionFee || '', mediaCommitment: pkg?.mediaCommitment || '',
        notes: pkg?.notes || '',
        status: pkg?.status || 'PUBLISHED',
        tier: pkg?.tier || '', saleType: pkg?.saleType || '',
        saleSubType: pkg?.saleSubType || '',
        videos: pkg?.videos != null ? String(pkg.videos) : '',
        minCreators: pkg?.minCreators != null ? String(pkg.minCreators) : '',
        sortOrder: pkg?.sortOrder != null ? String(pkg.sortOrder) : '',
        ...sheet,
      });
    }
  }, [open, pkg, reset]);

  const onSubmit = async (data: FormData) => {
    // Una variante con cualquier dato cargado se conserva (solo se descartan las vacías del todo).
    const cleanVariants = variants
      .filter(v => Object.entries(v).some(([k, val]) => k !== 'variantId' && (Array.isArray(val) ? val.length > 0 : val != null && String(val).trim() !== '')))
      .map(v => ({ ...v, name: v.name.trim(), tier: normCode(v.tier), deliverables: v.deliverables?.length ? v.deliverables : undefined }));
    const cleanLinks = links.map(l => ({ label: l.label.trim(), url: l.url.trim() })).filter(l => l.label && l.url);
    let payload: Record<string, unknown> = {
      name: data.name, description: data.description, targetAudience: data.targetAudience,
      programId: data.programId || undefined,
      availableMarkets: selectedMarkets,
      highlights: fromLines(data.highlights),
      includedProducts: fromLines(data.includedProducts),
      deliverables: data.deliverables ? fromLines(data.deliverables) : undefined,
      pricing: data.pricing || undefined, currency: data.currency || undefined,
      productionFee: data.productionFee || undefined,
      mediaCommitment: data.mediaCommitment || undefined,
      notes: data.notes || undefined,
      tier: normCode(data.tier),
      saleType: data.saleType || undefined,
      saleSubType: normCode(data.saleSubType),
      sortOrder: data.sortOrder !== '' && Number.isFinite(Number(data.sortOrder)) ? Math.max(0, Math.min(999, Math.trunc(Number(data.sortOrder)))) : undefined,
      videos: data.videos ? parseInt(data.videos, 10) : undefined,
      minCreators: data.minCreators ? parseInt(data.minCreators, 10) : undefined,
      variants: cleanVariants,
      links: cleanLinks,
      eligibility: {
        segments: segments,
        accountSizes: accountSizes,
        clientType: (clientType || undefined) as PackageClientType | undefined,
      },
      status: data.status,
    };
    // Los campos de la ficha, por ruta ("conditions.minRoi"): vacío = borrar.
    for (const s of PACKAGE_SHEET) for (const f of s.fields) payload = setPath(payload, f.path, data[f.key]?.trim() || undefined);
    // En una edición, lo vaciado viaja como null (undefined se pierde en el JSON y quedaba el valor
    // viejo): objetos anidados vacíos como {} y los opcionales simples como null.
    if (isEditing) {
      for (const k of ['eligibility', 'conditions', 'economics', 'externalView'] as const) payload[k] = payload[k] ?? {};
      const simple = ['programId', 'pricing', 'currency', 'effectiveDate', 'productionFee', 'mediaCommitment', 'driveUrl', 'notes', 'saleType', 'videos', 'minCreators', 'context', 'workflow', 'partnerRules', 'restrictions', 'validUntil'] as const;
      for (const k of simple) payload[k] = payload[k] ?? null;
      payload.deliverables = payload.deliverables ?? [];
    }

    if (isEditing) {
      // En una edición, vacío = borrar (null); undefined se pierde en el JSON y quedaba el valor viejo.
      await update.mutateAsync({
        packageId: pkg!.packageId,
        ...(payload as any),
        tier: (payload.tier as string | undefined) ?? null,
        saleSubType: (payload.saleSubType as string | undefined) ?? null,
        sortOrder: (payload.sortOrder as number | undefined) ?? null,
        salesModel: (payload.salesModel as string | undefined) ?? null,
        expectedUpdatedAt: pkg!.updatedAt,
      });
    } else {
      await create.mutateAsync({ partnerId, ...(payload as any) });
    }
    onOpenChange(false);
  };

  const isBusy = create.isPending || update.isPending;
  const sectionOpen = (s: PackageSheetSection) => !!pkg && s.fields.some(f => { const v = getPath(pkg, f.path); return v != null && String(v) !== ''; });
  const internalBadge = <InternalBadge className="ml-1" />;

  /** Un campo simple de la ficha según su tipo (text / textarea / url / date / select). */
  const renderField = (f: PackageSheetField) => (
    <div key={f.key} className={`space-y-1.5 ${f.wide ? 'sm:col-span-2' : ''}`}>
      <Label>{fieldLabel(t, f)}</Label>
      {f.type === 'textarea' ? (
        <Textarea {...register(f.key)} rows={3} />
      ) : f.type === 'select' ? (
        <Select value={watch(f.key) || NONE} onValueChange={v => setValue(f.key, v === NONE ? '' : v)}>
          <SelectTrigger><SelectValue placeholder={t('partnerships.package_form.no_value')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{t('partnerships.package_form.no_value')}</SelectItem>
            {(f.options ?? []).map(o => <SelectItem key={o} value={o}>{enumLabel(t, o)}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <Input {...register(f.key)} type={f.type === 'url' ? 'url' : f.type === 'date' ? 'date' : 'text'} placeholder={f.type === 'url' ? 'https://' : undefined} />
      )}
      {f.help && <p className="text-xs text-muted-foreground">{fieldHelp(t, f)}</p>}
    </div>
  );

  const simpleSection = (s: PackageSheetSection) => (
    <FormSection key={s.key} title={sectionTitle(t, s)} open={sectionOpen(s)} badge={s.internal ? internalBadge : undefined}>
      <div className="grid gap-3 sm:grid-cols-2">{s.fields.map(renderField)}</div>
    </FormSection>
  );
  const section = (key: string) => PACKAGE_SHEET.find(s => s.key === key)!;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('partnerships.package_form.title_edit') : t('partnerships.package_form.title_create')}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit(onSubmit)}
          // Un campo inválido (URL, número) dentro de una sección plegada no es enfocable y el
          // navegador bloqueaba el submit en silencio: se abre la sección antes de que lo enfoque.
          onInvalidCapture={e => { const d = (e.target as HTMLElement).closest('details'); if (d && !d.open) d.open = true; }}
          className="space-y-3"
        >

          {/* ── Básico ── */}
          <FormSection title={t('partnerships.package_form.section_basics')} open>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('partnerships.common.name_required')}</Label>
                <Input {...register('name', { required: true })} />
                {errors.name && <p className="text-xs text-destructive">{t('partnerships.common.required_field')}</p>}
              </div>
              {programs.length > 0 && (
                <div className="space-y-1.5">
                  <Label>{t('partnerships.package_form.program_label')}</Label>
                  <Select value={programValue || NONE} onValueChange={v => setValue('programId', v === NONE ? '' : v)}>
                    <SelectTrigger><SelectValue placeholder={t('partnerships.package_form.no_program')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>{t('partnerships.package_form.no_program')}</SelectItem>
                      {programs.map(p => <SelectItem key={p.programId} value={p.programId}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('partnerships.package_form.sale_type_label')}</Label>
                <Select value={watch('saleType') || NONE} onValueChange={v => setValue('saleType', v === NONE ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder={t('partnerships.package_form.no_classification')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('partnerships.package_form.no_classification')}</SelectItem>
                    <SelectItem value="agency_sold">{t('partnerships.package_form.agency_sold')}</SelectItem>
                    <SelectItem value="google_sold">{t('partnerships.package_form.google_sold')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t('partnerships.package_form.sale_sub_type_label')}</Label>
                <CodeSelect
                  value={watch('saleSubType') || ''}
                  onChange={v => setValue('saleSubType', v)}
                  options={subTypeOptions}
                  noneLabel={t('partnerships.package_form.no_sub_type')}
                  otherLabel={t('partnerships.package_form.other_option')}
                  placeholder={t('partnerships.package_form.sale_sub_type_placeholder')}
                />
                <p className="text-xs text-muted-foreground">{t('partnerships.package_form.sale_sub_type_help')}</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>{t('partnerships.common.description_label')}</Label>
              <Textarea {...register('description')} rows={2} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('partnerships.package_form.sort_order_label')}</Label>
                <Input {...register('sortOrder')} type="number" min="0" max="999" placeholder="1" className="w-32" />
                <p className="text-xs text-muted-foreground">{t('partnerships.package_form.sort_order_help')}</p>
              </div>
              <div className="space-y-1.5">
                <Label>{t('partnerships.common.status_label')}</Label>
                <Select value={statusValue} onValueChange={v => setValue('status', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </FormSection>

          {/* ── 1. Contexto ── */}
          {simpleSection(section('context'))}

          {/* ── 2. Variantes: entregables y precio por tier ── */}
          <FormSection title={sectionTitle(t, section('variants'))} open={!!(pkg?.variants?.length || pkg?.videos || pkg?.pricing)}>
            <RepeatableList<PackageVariant>
              items={variants}
              onChange={setVariants}
              makeEmpty={emptyVariant}
              addLabel={t('partnerships.package_form.variant_add')}
              emptyHint={t('partnerships.package_form.variant_empty')}
              max={20}
              renderItem={(v, update) => (
                <div className="grid gap-2 sm:grid-cols-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('partnerships.package_form.variant_name')}</Label>
                    <Input value={v.name} onChange={e => update({ name: e.target.value })} placeholder={t('partnerships.package_form.variant_name_placeholder')} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('partnerships.package_form.tier_label')}</Label>
                    <CodeSelect
                      value={v.tier || ''}
                      onChange={tier => update({ tier })}
                      options={tierOptions}
                      noneLabel={t('partnerships.package_form.no_tier')}
                      otherLabel={t('partnerships.package_form.other_option')}
                      placeholder={t('partnerships.package_form.bronze_silver_gold_platinum')}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">{t('partnerships.package_form.videos_label')}</Label>
                      <Input type="number" min="0" value={v.videos ?? ''} onChange={e => update({ videos: e.target.value ? parseInt(e.target.value, 10) : undefined })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('partnerships.package_form.min_creators_label')}</Label>
                      <Input type="number" min="0" value={v.minCreators ?? ''} onChange={e => update({ minCreators: e.target.value ? parseInt(e.target.value, 10) : undefined })} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('partnerships.package_form.price_label')}</Label>
                    <Input value={v.pricing ?? ''} onChange={e => update({ pricing: e.target.value || undefined })} placeholder={t('partnerships.package_form.usd_1_440')} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('partnerships.package_form.currency_label')}</Label>
                    <Input value={v.currency ?? ''} onChange={e => update({ currency: e.target.value || undefined })} placeholder="USD" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">{t('partnerships.packages.production_fee')}</Label>
                      <Input value={v.productionFee ?? ''} onChange={e => update({ productionFee: e.target.value || undefined })} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">{t('partnerships.packages.media_commitment')}</Label>
                      <Input value={v.mediaCommitment ?? ''} onChange={e => update({ mediaCommitment: e.target.value || undefined })} />
                    </div>
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-xs">{t('partnerships.package_form.deliverables_label')}</Label>
                    <Textarea rows={2} value={toLines(v.deliverables)} onChange={e => update({ deliverables: fromLines(e.target.value) })} placeholder={t('partnerships.package_form.deliverables_placeholder')} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('partnerships.package_form.variant_notes')}</Label>
                    <Textarea rows={2} value={v.notes ?? ''} onChange={e => update({ notes: e.target.value || undefined })} />
                  </div>
                </div>
              )}
            />
            {variants.length === 0 && (
              <div className="space-y-3 rounded-md border border-dashed border-border/60 p-3">
                <p className="text-xs text-muted-foreground">{t('partnerships.package_form.single_pack_hint')}</p>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t('partnerships.package_form.tier_label')}</Label>
                    <CodeSelect
                      value={watch('tier') || ''}
                      onChange={v => setValue('tier', v)}
                      options={tierOptions}
                      noneLabel={t('partnerships.package_form.no_tier')}
                      otherLabel={t('partnerships.package_form.other_option')}
                      placeholder={t('partnerships.package_form.bronze_silver_gold_platinum')}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('partnerships.package_form.videos_label')}</Label>
                    <Input {...register('videos')} type="number" min="0" placeholder="12" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('partnerships.package_form.min_creators_label')}</Label>
                    <Input {...register('minCreators')} type="number" min="0" placeholder="4" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('partnerships.package_form.price_label')}</Label>
                    <Input {...register('pricing')} placeholder={t('partnerships.package_form.usd_1_440')} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('partnerships.package_form.currency_label')}</Label>
                    <Input {...register('currency')} placeholder="USD" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('partnerships.package_form.production_fee_label')}</Label>
                    <Input {...register('productionFee')} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t('partnerships.package_form.media_commitment_label')}</Label>
                    <Input {...register('mediaCommitment')} />
                  </div>
                  <div className="space-y-1.5 col-span-2">
                    <Label>{t('partnerships.package_form.deliverables_label')}</Label>
                    <Textarea {...register('deliverables')} rows={2} placeholder={t('partnerships.package_form.deliverables_placeholder')} />
                  </div>
                </div>
              </div>
            )}
          </FormSection>

          {/* ── 3–4. SLAs / workflow, modelo de venta ── */}
          {simpleSection(section('workflow'))}
          {simpleSection(section('sales'))}

          {/* ── 5. Elegibilidad ── */}
          <FormSection title={sectionTitle(t, section('eligibility'))} open={!!(pkg?.eligibility || pkg?.availableMarkets.length || pkg?.targetAudience)}>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t('partnerships.package_form.segments_label')}</Label>
                <TagInput value={segments} onChange={setSegments} suggestions={[...facets.segments.keys()]} placeholder={t('partnerships.package_form.tag_placeholder')} listId="pkg-segments" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('partnerships.package_form.account_sizes_label')}</Label>
                <TagInput value={accountSizes} onChange={setAccountSizes} suggestions={[...facets.accountSizes.keys()]} placeholder={t('partnerships.package_form.tag_placeholder')} listId="pkg-sizes" />
              </div>
              <div className="space-y-1.5">
                <Label>{t('partnerships.package_form.client_type_label')}</Label>
                <Select value={clientType || NONE} onValueChange={v => setClientType(v === NONE ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder={t('partnerships.package_form.no_value')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>{t('partnerships.package_form.no_value')}</SelectItem>
                    {PACKAGE_CLIENT_TYPES.map(o => <SelectItem key={o} value={o}>{enumLabel(t, o)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t('partnerships.common.available_markets_label')}</Label>
                <div className="flex flex-wrap gap-1.5 p-2 border rounded-md min-h-10">
                  {markets.filter(m => m.active).map(m => (
                    <button
                      key={m.marketId}
                      type="button"
                      onClick={() => setSelectedMarkets(prev =>
                        prev.includes(m.name) ? prev.filter(n => n !== m.name) : [...prev, m.name]
                      )}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${
                        selectedMarkets.includes(m.name)
                          ? 'bg-foreground text-background border-foreground'
                          : 'border-muted-foreground/30 hover:border-foreground/50'
                      }`}
                    >
                      {m.name}
                    </button>
                  ))}
                  {markets.filter(m => m.active).length === 0 && (
                    <p className="text-xs text-muted-foreground self-center">{t('partnerships.common.no_markets_configured')}</p>
                  )}
                </div>
                {selectedMarkets.length > 0 && (
                  <p className="text-xs text-muted-foreground">{t('partnerships.common.selected_count', { count: selectedMarkets.length })}</p>
                )}
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t('partnerships.package_form.target_audience_label')}</Label>
                <Textarea {...register('targetAudience')} rows={2} />
              </div>
              {section('eligibility').fields.map(renderField)}
            </div>
          </FormSection>

          {/* ── 6–10. Condiciones, economics (INTERNO), vista externa, reglas, restricciones ── */}
          {simpleSection(section('conditions'))}
          {simpleSection(section('economics'))}
          {simpleSection(section('external'))}
          {simpleSection(section('partnerRules'))}
          {simpleSection(section('restrictions'))}

          {/* ── Destacados y productos incluidos (de siempre) ── */}
          <FormSection title={t('partnerships.package_form.section_highlights')} open={!!(pkg?.highlights.length || pkg?.includedProducts.length)}>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t('partnerships.package_form.highlights_label')}</Label>
                <Textarea {...register('highlights')} rows={4} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('partnerships.package_form.included_products_label')}</Label>
                <Textarea {...register('includedProducts')} rows={4} />
              </div>
            </div>
          </FormSection>

          {/* ── 11. Vigencia ── */}
          {simpleSection(section('validity'))}

          {/* ── 12. Links útiles ── */}
          <FormSection title={sectionTitle(t, section('links'))} open={!!(pkg?.driveUrl || pkg?.links?.length)}>
            <div className="grid gap-3 sm:grid-cols-2">{section('links').fields.map(renderField)}</div>
            <RepeatableList<PackageLink>
              items={links}
              onChange={setLinks}
              makeEmpty={emptyLink}
              addLabel={t('partnerships.package_form.links_add')}
              emptyHint={t('partnerships.package_form.links_empty')}
              max={20}
              renderItem={(l, update) => (
                <div className="grid gap-2 sm:grid-cols-[1fr_2fr]">
                  <Input value={l.label} onChange={e => update({ label: e.target.value })} placeholder={t('partnerships.package_form.link_label')} />
                  <Input value={l.url} onChange={e => update({ url: e.target.value })} type="url" placeholder="https://" />
                </div>
              )}
            />
          </FormSection>

          <FormSection title={t('partnerships.common.internal_notes_optional_label')} open={!!pkg?.notes} badge={internalBadge}>
            <Textarea {...register('notes')} rows={2} />
          </FormSection>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('common.ui.cancel')}</Button>
            <Button type="submit" disabled={isBusy}>
              {isBusy ? t('partnerships.common.saving') : isEditing ? t('partnerships.common.save_changes') : t('partnerships.package_form.create_button')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
